import React, { Context } from 'react';
import { InternalAuthService } from './internal-auth.service';
import { AuthService } from './auth.service';
import { ArticleService } from './article.service';
import { Dispatch } from 'redux';
import { RoleService } from './role.service';
import { AbilityService } from './ability.service';
import { SystemConfigurationService } from './systemConfiguration.service';
import { FileService } from './file.service';
import { UserService } from './user.service';
import { InternalClassifierService } from './internal-classifier.service';
import { EmailTemplateService } from './email-template.service';
import { EventService } from './event.service';
import { WeatherService } from './weather.service';
import { ProfileService } from './profile.service';
import { WarehouseService } from './warehouse.service';
import { DealService } from './deal.service';
import { EmployeeService } from './employee.service';
import { PartnerService } from './partner.service';
import { PartnerDepartmentService } from './partner-department.service';
import { FarmUserService } from './farm-user.service';
import { FarmService } from './farm.service';
import { ClassifierService } from './classifier.service';
import { ProductService } from './product.service';
import { EquipmentService } from './equipment.service';
import { FieldService } from './field.service';
import { FieldAnalysesService } from './fieldAnalyses.service';
import { FinanceModelingService } from './finance-modeling.service';
import { WorkService } from './work.service';
import { MaintenanceService } from './maintenance.service';
import { RemainderService } from './remainder.service';
import { TokenService } from './token.service';
import { CargoService } from './cargo.service';
import { AuditService } from './audit.service';
import { AnimalService } from './animal.service';
import { GroupAnimalService } from './group-animal.service';
import { FodderNeedService } from './fodder-need.service';
import { FertilizationPlanService } from './fertilization-plan.service';
import { CropChangePlanService } from './crop-change-plan.service';
import { BarnService } from './barn.service';
import { VzdService } from './vzd.service';
import { VidService } from './vid.service';
import { ReportJournalService } from './report-journal.service';
import { BudgetService } from './budget.service';
import { VaadLizReportService } from './vaad-liz-report.service';
import { VaadLizReportV2Service } from './vaad-liz-report-v2.service';
import { HealthService } from './health.service';
import { MailerLiteService } from './mailer-lite.service';
import { LivestockSummaryService } from './livestock-summary.service';
import { LivestockMetabolismService } from './livestock-metabolism.service';
import { LivestockSomaticCellsService } from './livestock-somatic-cells.service';
import { LivestockReproductionService } from './livestock-reproduction.service';
import { SummaryTaskService } from './summary-task.service';
import { DocumentStorageService } from './document-storage.service';
import { AnimalEventService } from './animal-event.service';
import { AnimalConfigurationService } from './animal-configuration.service';

const { createContext, useContext } = React;

interface ServiceContext {
  internalAuthService: InternalAuthService;
  auditService: AuditService;
  userService: UserService;
  roleService: RoleService;
  internalClassifierService: InternalClassifierService;
  abilityService: AbilityService;
  emailTemplateService: EmailTemplateService;
  systemPropertyService: SystemConfigurationService;
  fileService: FileService;
  authService: AuthService;
  articleService: ArticleService;
  eventService: EventService;
  weatherService: WeatherService;
  classifierService: ClassifierService;
  farmService: FarmService;
  profileService: ProfileService;
  dealService: DealService;
  remainderService: RemainderService;
  farmUserService: FarmUserService;
  warehouseService: WarehouseService;
  employeeService: EmployeeService;
  partnerService: PartnerService;
  partnerDepartmentService: PartnerDepartmentService;
  productService: ProductService;
  equipmentService: EquipmentService;
  fertilizationPlanService: FertilizationPlanService;
  fieldService: FieldService;
  fieldAnalysesService: FieldAnalysesService;
  financeModelingService: FinanceModelingService;
  workService: WorkService;
  maintenanceService: MaintenanceService;
  tokenService: TokenService;
  cargoService: CargoService;
  animalService: AnimalService;
  groupAnimalService: GroupAnimalService;
  fodderNeedService: FodderNeedService;
  cropChangePlanService: CropChangePlanService;
  barnService: BarnService;
  vzdService: VzdService;
  vidService: VidService;
  reportJournalService: ReportJournalService;
  vaadLizReportService: VaadLizReportService;
  vaadLizReportV2Service: VaadLizReportV2Service;
  budgetService: BudgetService;
  healthService: HealthService;
  mailerLiteService: MailerLiteService;
  livestockSummaryService: LivestockSummaryService;
  livestockMetabolismService: LivestockMetabolismService;
  livestockSomaticCellsService: LivestockSomaticCellsService;
  livestockReproductionService: LivestockReproductionService;
  summaryTaskService: SummaryTaskService;
  documentStorageService: DocumentStorageService;
  animalEventService: AnimalEventService;
  animalConfigurationService: AnimalConfigurationService;
}

let serviceContext: Context<ServiceContext> = null;

export default function setupServiceContext(dispatch: Dispatch) {
  serviceContext = createContext<ServiceContext>({
    internalAuthService: new InternalAuthService(dispatch),
    auditService: new AuditService(dispatch),
    userService: new UserService(dispatch),
    roleService: new RoleService(dispatch),
    internalClassifierService: new InternalClassifierService(dispatch),
    abilityService: new AbilityService(dispatch),
    emailTemplateService: new EmailTemplateService(dispatch),
    systemPropertyService: new SystemConfigurationService(dispatch),
    fileService: new FileService(dispatch),
    authService: new AuthService(dispatch),
    articleService: new ArticleService(dispatch),
    eventService: new EventService(dispatch),
    weatherService: new WeatherService(dispatch),
    classifierService: new ClassifierService(dispatch),
    farmService: new FarmService(dispatch),
    profileService: new ProfileService(dispatch),
    dealService: new DealService(dispatch),
    remainderService: new RemainderService(dispatch),
    farmUserService: new FarmUserService(dispatch),
    warehouseService: new WarehouseService(dispatch),
    employeeService: new EmployeeService(dispatch),
    partnerService: new PartnerService(dispatch),
    partnerDepartmentService: new PartnerDepartmentService(dispatch),
    productService: new ProductService(dispatch),
    equipmentService: new EquipmentService(dispatch),
    fertilizationPlanService: new FertilizationPlanService(dispatch),
    fieldService: new FieldService(dispatch),
    fieldAnalysesService: new FieldAnalysesService(dispatch),
    financeModelingService: new FinanceModelingService(dispatch),
    workService: new WorkService(dispatch),
    maintenanceService: new MaintenanceService(dispatch),
    tokenService: new TokenService(dispatch),
    cargoService: new CargoService(dispatch),
    animalService: new AnimalService(dispatch),
    groupAnimalService: new GroupAnimalService(dispatch),
    barnService: new BarnService(dispatch),
    fodderNeedService: new FodderNeedService(dispatch),
    cropChangePlanService: new CropChangePlanService(dispatch),
    vzdService: new VzdService(dispatch),
    vidService: new VidService(dispatch),
    reportJournalService: new ReportJournalService(dispatch),
    vaadLizReportService: new VaadLizReportService(dispatch),
    vaadLizReportV2Service: new VaadLizReportV2Service(dispatch),
    budgetService: new BudgetService(dispatch),
    healthService: new HealthService(dispatch),
    mailerLiteService: new MailerLiteService(dispatch),
    livestockSummaryService: new LivestockSummaryService(dispatch),
    livestockMetabolismService: new LivestockMetabolismService(dispatch),
    livestockSomaticCellsService: new LivestockSomaticCellsService(dispatch),
    livestockReproductionService: new LivestockReproductionService(dispatch),
    summaryTaskService: new SummaryTaskService(dispatch),
    documentStorageService: new DocumentStorageService(dispatch),
    animalEventService: new AnimalEventService(dispatch),
    animalConfigurationService: new AnimalConfigurationService(dispatch),
  });

  return serviceContext;
}

export const useService = (): ServiceContext => {
  return useContext<ServiceContext>(serviceContext);
};
